import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Info } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { whatsappUrl, telUrl, mailUrl, OFFICE } from "../lib/office";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Appointment() {
  const { t } = useLang();
  const location = useLocation();
  const preselectedService = location.state?.serviceType || "";

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    service_type: preselectedService,
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  const services = t("appointment.services") || [];

  const onChange = (field) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: v }));
  };

  // Build a prefilled WhatsApp message from the form contents
  const buildWhatsappMessage = () => {
    const lines = [];
    lines.push(t("whatsappMessages.appointment"));
    if (form.full_name) lines.push(`${t("appointment.form.fullName")}: ${form.full_name}`);
    if (form.service_type) lines.push(`${t("appointment.form.serviceType")}: ${form.service_type}`);
    if (form.preferred_date)
      lines.push(`${t("appointment.form.preferredDate")}: ${form.preferred_date}`);
    if (form.preferred_time)
      lines.push(`${t("appointment.form.preferredTime")}: ${form.preferred_time}`);
    if (form.message) lines.push(`${t("appointment.form.message")}: ${form.message}`);
    return lines.join("\n");
  };

  const buildMailBody = () => {
    const subject = t("common.bookAppointment");
    const body = buildWhatsappMessage();
    return `mailto:${OFFICE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    toast.info(t("appointment.contactHint"), {
      description: t("appointment.alt"),
    });
    // Open WhatsApp with prefilled summary for quickest contact
    window.open(whatsappUrl(buildWhatsappMessage()), "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <section className="border-b border-border-light">
        <div className="container-notary py-20 md:py-24">
          <div className="overline mb-4">{t("appointment.eyebrow")}</div>
          <h1 className="text-4xl md:text-5xl font-serif text-charcoal max-w-3xl">
            {t("appointment.title")}
          </h1>
          <span className="divider-gold my-6 block" />
          <p className="text-lg text-charcoal-muted max-w-2xl">{t("appointment.subtitle")}</p>
        </div>
      </section>

      <section className="container-notary py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <form
          onSubmit={onSubmit}
          className="lg:col-span-8 border border-border-light p-8 md:p-12 bg-surface"
          data-testid="appointment-form"
        >
          <div className="mb-8 flex items-start gap-3 bg-surface-muted border border-border-light px-4 py-3 text-sm text-charcoal-muted">
            <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <div>{t("appointment.contactHint")}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="field-label">{t("appointment.form.fullName")}</label>
              <input
                type="text"
                value={form.full_name}
                onChange={onChange("full_name")}
                className="field-input"
                data-testid="appointment-fullname"
              />
            </div>
            <div>
              <label className="field-label">{t("appointment.form.phone")}</label>
              <input
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                className="field-input"
                data-testid="appointment-phone"
              />
            </div>
            <div>
              <label className="field-label">{t("appointment.form.email")}</label>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="field-input"
                data-testid="appointment-email"
              />
            </div>
            <div>
              <label className="field-label">{t("appointment.form.serviceType")}</label>
              <Select
                value={form.service_type}
                onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
              >
                <SelectTrigger className="field-input h-12 rounded-sm" data-testid="appointment-service-trigger">
                  <SelectValue placeholder={t("appointment.form.serviceTypePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`appointment-service-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="field-label">{t("appointment.form.preferredDate")}</label>
              <input
                type="date"
                value={form.preferred_date}
                onChange={onChange("preferred_date")}
                className="field-input"
                data-testid="appointment-date"
              />
            </div>
            <div>
              <label className="field-label">{t("appointment.form.preferredTime")}</label>
              <input
                type="time"
                value={form.preferred_time}
                onChange={onChange("preferred_time")}
                className="field-input"
                data-testid="appointment-time"
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t("appointment.form.message")}</label>
              <textarea
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                placeholder={t("appointment.form.messagePlaceholder")}
                className="field-input resize-none"
                data-testid="appointment-message"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button type="submit" className="btn-gold" data-testid="appointment-submit">
              <MessageCircle className="w-4 h-4" />
              {t("appointment.sendViaWhatsapp")}
            </button>
            <a href={telUrl(true)} className="btn-outline-dark" data-testid="appointment-call-alt">
              <Phone className="w-4 h-4" />
              {t("common.call")}
            </a>
            <a href={buildMailBody()} className="btn-ghost" data-testid="appointment-mail-alt">
              <Mail className="w-4 h-4" />
              {t("common.email")}
            </a>
          </div>
        </form>

        <aside className="lg:col-span-4 space-y-4" data-testid="appointment-alt">
          <div className="overline mb-2">{t("appointment.alt")}</div>
          <a
            href={telUrl(true)}
            className="flex items-center gap-4 border border-border-light p-5 bg-surface hover:border-gold transition-colors"
            data-testid="appointment-side-call"
          >
            <Phone className="w-5 h-5 text-gold" />
            <div>
              <div className="font-serif text-charcoal">{t("common.call")}</div>
              <div className="text-xs text-charcoal-muted">{OFFICE.phoneFixed}</div>
            </div>
          </a>
          <a
            href={whatsappUrl(t("whatsappMessages.appointment"))}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 border border-border-light p-5 bg-surface hover:border-gold transition-colors"
            data-testid="appointment-side-whatsapp"
          >
            <MessageCircle className="w-5 h-5 text-gold" />
            <div>
              <div className="font-serif text-charcoal">{t("common.whatsapp")}</div>
              <div className="text-xs text-charcoal-muted">{OFFICE.phoneMobile}</div>
            </div>
          </a>
          <a
            href={mailUrl(t("common.bookAppointment"))}
            className="flex items-center gap-4 border border-border-light p-5 bg-surface hover:border-gold transition-colors"
            data-testid="appointment-side-email"
          >
            <Mail className="w-5 h-5 text-gold" />
            <div>
              <div className="font-serif text-charcoal">{t("common.email")}</div>
              <div className="text-xs text-charcoal-muted">{OFFICE.email}</div>
            </div>
          </a>
          <Link
            to="/contact"
            className="block border border-border-light p-5 bg-surface-muted hover:border-gold transition-colors text-charcoal text-sm"
          >
            {t("nav.contact")} →
          </Link>
        </aside>
      </section>
    </div>
  );
}
